/**
 * Geldbetraege des Rechnungsmoduls — ganzzahlige Cent, nichts anderes.
 *
 * Warum `number` und nicht `bigint`:
 *  - `bigint` ueberlebt JSON.stringify nicht (wirft TypeError). Rechnungszeilen
 *    und Details liegen als jsonb in der DB — ein einziges bigint im Payload
 *    wuerde den kompletten Save killen.
 *  - PostgREST liefert bigint ohnehin als JSON-String, der Mapper muss also so
 *    oder so konvertieren. Durchgaengig `number` heisst eine Konvertierung
 *    statt zwei.
 *  - Number.MAX_SAFE_INTEGER entspricht rund 90 Mrd. EUR — drei Groessen-
 *    ordnungen mehr als jede realistische Rechnung.
 *
 * Die Gegenleistung fuer diese Entscheidung ist `assertCents`: jeder Wert, der
 * aus der DB oder aus dem Float-Bestands-LV kommt, wird an genau einer Stelle
 * geprueft, statt spaeter als 0.30000000000000004 im PDF zu stehen.
 */

/** Ganzzahliger Cent-Betrag. Branding nur per Konvention, nicht per Typ-System. */
export type Cents = number;

/**
 * Stellt sicher, dass ein Wert ein ganzzahliger, sicher darstellbarer Cent-Betrag
 * ist. Wirft mit Kontext, weil ein stiller Fallback auf 0 in einem festgeschriebenen
 * Beleg schlimmer waere als ein Abbruch.
 */
export const assertCents = (value: number, context: string): Cents => {
    if (!Number.isSafeInteger(value)) {
        throw new Error(`Betrag ist kein ganzzahliger Cent-Wert (${context}): ${value}`);
    }
    return value;
};

/**
 * Kaufmaennische Rundung auf ganze Cent (half away from zero), also 0,5 immer
 * vom Nullpunkt weg. Math.round allein rundet -0.5 auf -0 statt auf -1 und
 * waere damit bei Gutschriften und Storno asymmetrisch.
 *
 * Der Nudge korrigiert dieselbe IEEE-754-Falle wie round2 in quoteLogic.ts:
 * 1.005 * 100 ergibt 100.49999999999999 und wuerde sonst auf 100 abgerundet.
 *
 * Anders als round2 wird hier RELATIV genudged (abs * EPSILON), nicht absolut.
 * round2 addiert Number.EPSILON, solange der Wert noch die Groessenordnung 1
 * hat — dort ist EPSILON groesser als der Abstand zum naechsten Float. Hier
 * kommt der Wert bereits mit 100 an, wo ein ULP rund 1.4e-14 betraegt und ein
 * absolutes EPSILON von 2.2e-16 spurlos verpufft.
 *
 * Der Nudge ist nach oben gedeckelt. Ohne Deckel waechst er mit dem Betrag und
 * uebersteigt irgendwann den Abstand zur .5-Grenze: bei 1000000000.4999998
 * (rund 10 Mio. Euro) wuerde relativ genudged auf 1000000001 aufgerundet,
 * obwohl der Wert unter der halben Cent-Grenze liegt. Oberhalb der Deckelung
 * ist der Float ohnehin so grob, dass eine Korrektur mehr schadet als nuetzt.
 */
const MAX_NUDGE = 1e-9;

export const roundCents = (value: number): Cents => {
    if (!Number.isFinite(value)) return 0;
    const abs = Math.abs(value);
    const rounded = Math.round(abs + Math.min(abs * Number.EPSILON, MAX_NUDGE));
    return value < 0 ? -rounded : rounded;
};

/**
 * Euro-Float (wie im Bestands-LV: ServiceItem.unitPrice) nach Cent.
 *
 * Das ist die EINZIGE erlaubte Stelle, an der ein Float zu Geld wird. Danach
 * wird ausschliesslich in Cent gerechnet — nie wieder aus dem Float abgeleitet,
 * sonst driften PDF, XML und LV um einzelne Cent auseinander.
 */
export const centsFromEuro = (euro: number): Cents => roundCents(euro * 100);

/** Cent nach Euro-Float. Nur fuer Anzeige und Alt-Schnittstellen, nie zum Weiterrechnen. */
export const euroFromCents = (cents: Cents): number => cents / 100;

/**
 * Parst eine Benutzereingabe nach Cent. Akzeptiert deutsche ("1.234,56") und
 * englische ("1234.56") Schreibweise sowie ein fuehrendes Minus und ein
 * angehaengtes Euro-Zeichen.
 *
 * Gibt null zurueck, wenn die Eingabe nicht als Zahl lesbar ist — der Aufrufer
 * entscheidet, ob das ein Formularfehler oder ein leeres Feld ist.
 *
 * Bewusste Festlegung fuer den mehrdeutigen Fall ohne Komma: "1.000" wird als
 * eintausend gelesen, nicht als eins Komma null. Die Oberflaeche ist deutsch,
 * und ein Punkt vor genau drei Ziffern ist dort ein Tausenderpunkt. Wer 1,00
 * meint, schreibt "1,00" oder "1.00" — letzteres hat nur zwei Nachkommastellen
 * und faellt damit nicht unter die Tausenderregel.
 */
export const centsFromEuroInput = (input: string): Cents | null => {
    const value = parseDecimalInput(input);
    return value == null ? null : roundCents(value * 100);
};

/**
 * Dieselbe Schreibweisen-Erkennung wie centsFromEuroInput, aber ohne Cent-
 * Rundung — fuer Groessen, die KEIN Geld sind (Mengen aus einem eingelesenen
 * Beleg). Eigene Ad-hoc-Parser driften an genau einer Stelle auseinander:
 * "1.5" naiv entpunktet ergibt 15 statt 1,5, also die zehnfache Menge.
 */
export const parseDecimalInput = (input: string): number | null => {
    const raw = (input ?? '').trim().replace(/\s/g, '').replace(/€|EUR/gi, '');
    if (!raw) return null;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    // Punkte, die als reine Tausendergliederung auftreten ("1.000", "1.234.567"):
    // Ziffern in Dreiergruppen, kein Komma im String.
    const dotsAreThousands = lastComma < 0 && /^-?\d{1,3}(\.\d{3})+$/.test(raw);
    const normalized =
        lastComma > lastDot || dotsAreThousands
            ? raw.replace(/\./g, '').replace(',', '.')
            : raw.replace(/,/g, '');

    if (!/^-?\d*\.?\d*$/.test(normalized) || !/\d/.test(normalized)) return null;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
};

/** "1.234,56" — Zahl ohne Waehrungszeichen, fuer Tabellenspalten mit eigener Kopfzeile. */
export const formatCentsPlain = (cents: Cents): string =>
    (cents / 100).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/** "1.234,56 €" — Standarddarstellung in UI und PDF. */
export const formatCents = (cents: Cents): string => `${formatCentsPlain(cents)} €`;

/** Summe einer Cent-Liste. Ganzzahlig, daher exakt — kein Rundungsfehler moeglich. */
export const sumCents = (values: Cents[]): Cents => values.reduce((acc, v) => acc + v, 0);

/**
 * Steuersatz in Basispunkten: 19 % = 1900.
 *
 * Ganzzahlig, weil Steuergruppen nach dem Satz gruppiert werden und 19.00 !== 19
 * bei Float-Vergleichen zwei getrennte Gruppen erzeugen wuerde.
 */
export const taxRateBpFromPercent = (percent: number): number => Math.round(percent * 100);

/** Basispunkte zurueck in Prozent, z.B. 1900 -> 19. Nur fuer Anzeige. */
export const percentFromTaxRateBp = (bp: number): number => bp / 100;

/** "19 %" bzw. "7,5 %" — Steuersatz fuer Beleg und UI, ohne unnoetige Nullen. */
export const formatTaxRateBp = (bp: number): string => {
    const percent = percentFromTaxRateBp(bp);
    const text = Number.isInteger(percent)
        ? String(percent)
        : percent.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    return `${text} %`;
};

/**
 * Steuerbetrag zu einer Bemessungsgrundlage.
 *
 * Bewusst auf der Gruppensumme aufgerufen, nicht je Position: die Spezifikation
 * (§10.3) verlangt Netto je Position, Steuer aber je Steuersatz aus der
 * Gruppensumme. Positionsweise Steuerrundung ergibt bei vielen Kleinbetraegen
 * eine andere Summe als das Finanzamt erwartet.
 */
export const taxFromNet = (netCents: Cents, taxRateBp: number): Cents =>
    roundCents((netCents * taxRateBp) / 10_000);

/**
 * Zerlegt einen Bruttobetrag in Netto und Steuer (Steuer herausrechnen).
 *
 * Die Steuer ergibt sich als Rest, nicht durch eine zweite Rundung — dadurch
 * gilt immer netCents + taxCents === grossCents, was fuer Zahlungsaufteilungen
 * und den DATEV-Export zwingend ist.
 */
export const splitGross = (
    grossCents: Cents,
    taxRateBp: number,
): { netCents: Cents; taxCents: Cents } => {
    const netCents = roundCents((grossCents * 10_000) / (10_000 + taxRateBp));
    return { netCents, taxCents: grossCents - netCents };
};
